import React, { useState, useEffect } from 'react';
import { contentAPI } from '../services/api';
import UpgradeModal from '../components/UpgradeModal';

const STATUSES  = ['IDEA','SCRIPTING','FILMING','EDITING','SCHEDULED','POSTED'];
const PLATFORMS = ['YouTube','Instagram','Both','Twitter','LinkedIn','Other'];
const EMPTY = { title:'', platform:'YouTube', status:'IDEA', scheduledDate:'', notes:'' };

export default function CalendarPage() {
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [filter, setFilter]     = useState('ALL');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [upgrade, setUpgrade]   = useState(null);

  const load = () => contentAPI.getAll().then(r => setPosts(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditing(null); setForm(EMPTY); setShowForm(true); setError(''); };
  const openEdit = (p) => { setEditing(p); setForm(p); setShowForm(true); setError(''); };
  const closeForm = () => { setShowForm(false); setEditing(null); setError(''); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError('Title is required');
    setSaving(true); setError('');
    try {
      if (editing) await contentAPI.update(editing.id, form);
      else         await contentAPI.create(form);
      await load(); closeForm();
    } catch (e) {
      const data = e.response?.data;
      if (data?.error === 'PLAN_LIMIT_REACHED') {
        closeForm();
        setUpgrade({ feature: data.feature, limit: data.limit });
      } else {
        setError('Failed to save.');
      }
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await contentAPI.delete(id); load();
  };

  const handleStatusChange = async (id, status) => {
    await contentAPI.updateStatus(id, status); load();
  };

  const filtered = filter === 'ALL' ? posts : posts.filter(p => p.status === filter);

  if (loading) return <div style={{ padding: '2rem', color: 'var(--muted)' }}>Loading...</div>;

  if (upgrade) return <UpgradeModal feature={upgrade.feature} limit={upgrade.limit} onClose={() => setUpgrade(null)} />;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">📅 Content Calendar</div>
          <div className="page-subtitle">Plan and track all your content from idea to posted</div>
        </div>
        <button className="btn btn-green" onClick={openAdd}>+ New Post</button>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {['ALL', ...STATUSES].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={s === 'ALL' ? 'btn btn-sm' : `btn btn-sm status-${s}`}
            style={{ border: filter === s ? '2px solid currentColor' : '2px solid transparent', background: s === 'ALL' ? (filter === 'ALL' ? 'var(--ink)' : 'var(--surface-2)') : undefined, color: s === 'ALL' ? (filter === 'ALL' ? 'white' : 'var(--muted)') : undefined }}>
            {s} ({s === 'ALL' ? posts.length : posts.filter(p => p.status === s).length})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📅</div>
          <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>No content planned</div>
          <div style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>Start planning your content calendar</div>
          <button className="btn btn-green" onClick={openAdd}>+ Add First Post</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(p => (
            <div key={p.id} className="card" style={{ padding: '1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{p.title}</div>
                    <span className={`badge status-${p.status}`}>{p.status}</span>
                    {p.platform && <span className="badge badge-gray">{p.platform}</span>}
                  </div>
                  {p.scheduledDate && <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>📆 {p.scheduledDate}</div>}
                  {p.notes && <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.2rem' }}>📝 {p.notes}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flexShrink: 0 }}>
                  <select value={p.status} onChange={e => handleStatusChange(p.id, e.target.value)}
                    style={{ padding: '0.35rem 0.6rem', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', background: 'white' }}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)} style={{ flex: 1 }}>Edit</button>
                    <button className="btn btn-sm" onClick={() => handleDelete(p.id)} style={{ background: 'var(--red-pale)', color: 'var(--red)', border: 'none' }}>✕</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div onClick={closeForm} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 'var(--r-xl)', width: '100%', maxWidth: '480px', padding: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.1rem' }}>{editing ? 'Edit Post' : '+ New Post'}</div>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
            </div>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Post Title *</label>
                <input className="form-input" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. How I learned Python in 30 days" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Platform</label>
                  <select className="form-input" value={form.platform} onChange={e => setForm(f => ({...f, platform: e.target.value}))}>
                    {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Status</label>
                  <select className="form-input" value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                    {STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Scheduled Date</label>
                <input className="form-input" type="date" value={form.scheduledDate} onChange={e => setForm(f => ({...f, scheduledDate: e.target.value}))} />
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Notes</label>
                <textarea className="form-input" rows={2} value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Ideas, references, reminders..." style={{ resize: 'vertical' }} />
              </div>
              {error && <div style={{ padding: '0.65rem', background: 'var(--red-pale)', borderRadius: 'var(--r-md)', fontSize: '0.82rem', color: 'var(--red)' }}>⚠️ {error}</div>}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-green" style={{ flex: 2 }} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Post'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
